<ng-template #overridesPopover>
  @for (override of scheduleOverrides; track override) {
    <span class="u_block">{{ override }}</span>
  }
</ng-template>
<main>
  <rv-draggable [draggableService]="draggableService">
    <div resizeObserver (resizeEvent)="onHeaderResize($event)">
      <div
        class="tw-flex tw-flex-col tw-px-[24px] tw-w-full md:!tw-flex-row tw-justify-between md:tw-items-center tw-py-[16px]"
      >
        <display-license-counts
          showOnlineCount="true"
          [includeSubcompanies]="allCompanies"
        ></display-license-counts>
        <mat-slide-toggle
          labelPosition="before"
          class="md:!tw-w-fit sm:!tw-w-full"
          id="allCompaniesCheckbox"
          (ngModelChange)="toggleAllCompanies($event)"
          [ngModel]="allCompanies"
          aria-labelledby="allCompaniesLabel"
        >
          <span id="allCompaniesLabel">Show all sub-companies</span>
        </mat-slide-toggle>
      </div>
      @if (error()) {
        <div
          id="errorBox"
          class="madero-style alert alert-danger text-center u_margin-md-top"
          role="alert"
        >
          <p>
            <strong>{{ error().errorMessage }}</strong>
          </p>
          {{ error().apiError }}
        </div>
      }

      <!-- Firmware Update Notifications -->
      <div class="tw-px-6">
        <app-firmware-update-notifications></app-firmware-update-notifications>
      </div>

      <mat-divider class="light" />
      <div
        class="tw-flex tw-flex-col md:tw-items-center md:!tw-flex-row tw-p-[16px] tw-gap-4"
      >
        <search-filter
          #searchFilter
          class="tw-w-full"
          [filterConfig]="filterConfig"
          [search]="searchState.getSearchObject()"
          [doSearch]="doSearch"
          [focus]="true"
        />
        @if (canUseFolders()) {
          <new-folder-button
            [parentId]="folderId"
            [existingFiles]="folders"
            kind="Display"
            (folderAdded)="onFolderAdded()"
          ></new-folder-button>
        }
        <button
          mat-flat-button
          type="button"
          id="displayAddButton"
          class="tw-min-w-72 tw-whitespace-nowrap"
          color="primary"
          uiSref="apps.displays.add"
          [uiParams]="{ folderId }"
          *requireAccess="'display:fullAccess'"
        >
          <mat-icon svgIcon="add" />
          Add Display
        </button>
      </div>
      <div
        class="tw-flex tw-flex-col tw-gap-4 tw-px-[16px] tw-border-solid tw-border-0 tw-border-t tw-border-gray-200"
      >
        <folder-path
          [folderTree]="folderService.folderPathTree"
          [folderId]="folderId"
          (folderClicked)="onFolderClicked($event)"
          rootName="Displays"
        ></folder-path>
      </div>
      <batch-actions />
    </div>
    <mat-divider class="light" />
    <div
      class="tw-px-3.5 displays-list-container"
      #displaysListContainer
      rvSpinner="displays-list-loader"
      [rvShowSpinner]="loadingItems()"
    >
      <cdk-virtual-scroll-viewport
        #scrollViewport
        itemSize="68"
        class="scrollable-list horizontal-scroll rounded-rows"
      >
        <table id="displaysListTable" class="table">
          <thead class="table-header">
            <tr class="table-header__row">
              <th
                class="table-header__cell col-sm-2 table-header__cell_{{
                  field.name
                }}"
                *ngFor="let field of tableColumns"
                id="tableHeader-{{ field.name }}"
                [ariaSort]="field.name"
                [sortBy]="searchState.getSearchObject().sortBy"
                [reverse]="searchState.getSearchObject().reverse"
              >
                <div
                  [class.flex-row]="field.name === 'name'"
                  [class.fixed-width]="field.name === 'name'"
                >
                  @if (field.name === 'name') {
                    <mat-checkbox
                      id="displays-select-all-cb"
                      (click)="$event.stopPropagation()"
                      (change)="toggleSelection()"
                      [checked]="listState.isAllSelected()"
                      [aria-label]="
                        listState.isAllSelected()
                          ? 'Deselect all'
                          : 'Select all'
                      "
                      [aria-describedby]="
                        'tableHeader-' + field.name + '-label'
                      "
                      [ariaChecked]="listState.isAllSelected()"
                      ariaCheckedElementId="displays-select-all-cb-input"
                      *requireRole="'!ap'"
                      class="mr-2"
                      matTooltip="Bulk select"
                      matTooltipShowDelay="500"
                      matTooltipClass="menu-tooltip"
                    />
                  }
                  <div
                    id="tableHeader-{{ field.name }}-label"
                    class="table-header-label"
                    [class.u_clickable]="field.sortable"
                    [attr.role]="field.sortable ? 'button' : null"
                    [attr.tabindex]="field.sortable ? 0 : null"
                    (keyup.enter)="sort(field)"
                    (keydown.space)="onSortSpaceKeydown(field, $event)"
                    (click)="sort(field)"
                  >
                    {{ field.label }}
                    @if (
                      field.sortable &&
                      searchState.getSearchObject().sortBy === field.name
                    ) {
                      <i
                        [attr.aria-label]="
                          searchState.getSearchObject().reverse
                            ? 'Descending order'
                            : 'Ascending order'
                        "
                        class="fa"
                        [ngClass]="{
                          'fa-caret-up': !searchState.getSearchObject().reverse,
                          'fa-caret-down':
                            searchState.getSearchObject().reverse,
                        }"
                      ></i>
                    }
                  </div>
                </div>
              </th>
              <th
                class="table-header__cell action-column"
                *requireAccess="'display:fullAccess'; allowSharedAccess: true"
              ></th>
            </tr>
          </thead>
          <tbody class="table-body">
            @if (
              !(
                !loadingItems() &&
                list().length === 0 &&
                searchState.getSearchObject().query === undefined
              )
            ) {
              <ng-container
                *cdkVirtualFor="let listItem of list(); let idx = index"
              >
                @if (listItem.isFolder) {
                  <tr
                    (click)="onFolderClicked(listItem.item.id)"
                    [rvDraggableItem]="listItem.item"
                    [rvDroppableItem]="listItem.item"
                    class="table-body__row clickable"
                    [class.selected]="listItem.selected"
                  >
                    <td
                      class="table-body__cell display-name draggable-mirror-content"
                    >
                      <div
                        class="tw-flex tw-flex-row tw-items-center tw-gap-[16px]"
                      >
                        <mat-icon
                          rv-draggable-handle
                          class="draggable-mirror-content-hidden"
                          svgIcon="drag-stroke"
                        />
                        <mat-checkbox
                          id="displays-cb-{{ idx }}"
                          class="draggable-mirror-content-hidden"
                          (click)="onCheckboxClick($event, listItem, idx)"
                          [checked]="listItem.selected"
                          [aria-label]="
                            listItem.selected
                              ? 'Deselect folder'
                              : 'Select folder'
                          "
                          [aria-describedby]="'folder-' + listItem.item.id"
                          [ariaChecked]="listItem.selected"
                          ariaCheckedElementId="displays-cb-{{ idx }}-input"
                          *requireRole="'!ap'"
                          matTooltip="Bulk select"
                          matTooltipShowDelay="500"
                          matTooltipClass="menu-tooltip"
                        />
                        <div
                          class="tw-flex tw-flex-row tw-items-center tw-gap-4"
                        >
                          <mat-icon svgIcon="folder" class="tw-text-gray-500" />
                          <a
                            id="folder-{{ listItem.item.id }}"
                            class="madero-link tw-font-medium u_ellipsis-lg"
                          >
                            {{ listItem.item.name }}
                          </a>
                        </div>
                      </div>
                    </td>
                    @if (allCompanies) {
                      <td>{{ listItem.item.companyName }}</td>
                    }
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td class="table-body__cell">
                      <change-details
                        [changeDate]="listItem.item.changeDate"
                        [changedBy]="listItem.item.changedBy"
                      />
                    </td>
                    <td>&nbsp;</td>
                    <td
                      class="table-body__cell action-column"
                      *requireAccess="
                        'display:fullAccess';
                        allowSharedAccess: true
                      "
                    >
                      <operations-menu
                        id="display-folder-operations-menu-{{ idx + 1 }}"
                        [operations]="folderOperations"
                        (operationClick)="
                          handleOperationClick($event, listItem)
                        "
                      />
                    </td>
                  </tr>
                } @else {
                  <tr
                    [rvDraggableItem]="listItem.item"
                    uiSref="apps.displays.change"
                    [uiParams]="{
                      displayId: listItem.item.id,
                      companyId: listItem.item.companyId,
                    }"
                    class="table-body__row clickable"
                    [class.selected]="listItem.selected"
                    [ngClass]="{
                      'bg-lighter-gray':
                        selectedCompayId !== listItem.item.companyId,
                    }"
                  >
                    <td
                      class="table-body__cell display-name draggable-mirror-content"
                    >
                      <div
                        class="tw-flex tw-flex-row tw-items-center tw-gap-[16px]"
                      >
                        <mat-icon
                          rv-draggable-handle
                          class="draggable-mirror-content-hidden"
                          svgIcon="drag-stroke"
                        />
                        <mat-checkbox
                          id="displays-cb-{{ idx }}"
                          class="draggable-mirror-content-hidden"
                          (click)="onCheckboxClick($event, listItem, idx)"
                          [checked]="listItem.selected"
                          [aria-label]="
                            listItem.selected
                              ? 'Deselect display'
                              : 'Select display'
                          "
                          [aria-describedby]="'display-' + listItem.item.id"
                          ariaCheckedElementId="displays-cb-{{ idx }}-input"
                          [ariaChecked]="listItem.selected"
                          *requireRole="'!ap'"
                          matTooltip="Bulk select"
                          matTooltipShowDelay="500"
                          matTooltipClass="menu-tooltip"
                        />
                        <a
                          id="display-{{ listItem.item.id }}"
                          uiSref="apps.displays.change"
                          [uiParams]="{
                            displayId: listItem.item.id,
                            companyId: listItem.item.companyId,
                          }"
                          (click)="$event.stopPropagation()"
                          class="tw-font-medium u_ellipsis-lg"
                        >
                          {{ listItem.item.name }}
                        </a>
                      </div>
                    </td>
                    @if (allCompanies) {
                      <td
                        id="companyName-{{ listItem.item.id }}"
                        class="table-body__cell"
                      >
                        <span>{{ listItem.item.companyName }}</span>
                      </td>
                    }
                    <td class="table-body__cell display-status">
                      @if (
                        displayService.showLicenseRequired(listItem.item) &&
                        listItem.item.onlineStatus !== 'not_activated'
                      ) {
                        <span> License Required </span>
                      } @else {
                        <display-status
                          [onlineStatus]="listItem.item.onlineStatus"
                          [screenPowerStatus]="listItem.item.screenPowerStatus"
                        ></display-status>
                      }
                    </td>
                    <td class="table-body__cell">
                      @if (displayService.showLicenseRequired(listItem.item)) {
                        <span> License Required </span>
                      } @else {
                        <ng-container
                          *ngTemplateOutlet="
                            lastConnection;
                            context: {
                              lastActivityDate: listItem.item.lastActivityDate,
                              onlineStatus: listItem.item.onlineStatus,
                            }
                          "
                        />
                      }
                    </td>
                    <td class="table-body__cell display-schedule">
                      <div class="u_nowrap flex-row">
                        @if (displayService.hasSchedule(listItem.item)) {
                          <a
                            class="u_ellipsis-md schedule-view"
                            uiSref="apps.schedules.details"
                            [uiParams]="{
                              scheduleId: listItem.item.scheduleId,
                              cid: listItem.item.companyId,
                            }"
                          >
                            {{ listItem.item.scheduleName }}
                          </a>
                        } @else {
                          <span class="u_ellipsis-md tw-text-gray-700">
                            {{
                              listItem.item.overrideScheduleNames?.length > 0
                                ? 'No Default Schedule'
                                : 'No Schedule Selected'
                            }}
                          </span>
                        }
                        @if (listItem.item.overrideScheduleNames?.length > 0) {
                          <mat-chip
                            class="badge schedule u_clickable ml-2"
                            [popover]="overridesPopover"
                            (onShown)="
                              showPopover(listItem.item.overrideScheduleNames)
                            "
                            containerClass="madero-style"
                            outsideClick="true"
                            (click)="$event.stopPropagation()"
                            placement="bottom right"
                          >
                            +{{ listItem.item.overrideScheduleNames.length }}
                          </mat-chip>
                        }
                      </div>
                    </td>
                    <td class="table-body__cell">
                      <change-details
                        [changeDate]="listItem.item.changeDate"
                        [changedBy]="listItem.item.changedBy"
                      />
                    </td>
                    <td class="table-body__cell">
                      <ng-container
                        *ngTemplateOutlet="
                          licenseStatus;
                          context: { item: listItem.item }
                        "
                      />
                    </td>
                    <td
                      class="table-body__cell action-column"
                      *requireAccess="
                        'display:fullAccess';
                        allowSharedAccess: true
                      "
                    >
                      <operations-menu
                        *requireAccess="
                          'display:fullAccess';
                          entityId: listItem.item.id
                        "
                        id="display-operations-menu-{{ idx + 1 }}"
                        [entityId]="listItem.item.id"
                        [listItem]="listItem"
                        (operationClick)="
                          handleOperationClick($event, listItem)
                        "
                        [operations]="listOperations.operations"
                      />
                    </td>
                  </tr>
                }
              </ng-container>
            }
            <!-- If no search results -->
            @if (!loadingItems() && list().length === 0) {
              <tr class="placeholder">
                <td colspan="6">
                  <div
                    class="tw-flex tw-flex-col tw-items-center tw-w-full tw-mb-20"
                  >
                    @if (searchState.getSearchObject().query === undefined) {
                      <img
                        src="../images/empty-display.svg"
                        aria-hidden="true"
                      />
                      <h2 class="tw-text-4xl">Activate your first display</h2>
                      <p
                        class="tw-block tw-w-1/2 tw-text-center tw-text-gray-600 tw-text-wrap"
                      >
                        To see your presentations on your display, click "Add
                        Display" and follow the instructions.
                      </p>
                      <a
                        id="emptyDisplayAddButton"
                        uiSref="apps.displays.add"
                        [uiParams]="{ folderId: folderId }"
                        mat-button
                        color="primary"
                      >
                        Add Display
                      </a>
                    } @else {
                      <no-results (action)="searchFilter.reset()"></no-results>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </cdk-virtual-scroll-viewport>
    </div>
  </rv-draggable>
</main>

<ng-template #licenseStatus let-item="item">
  <div class="tw-flex tw-flex-row tw-items-center tw-gap-[8px]">
    @if (hasDigitalSignageLicense(item)) {
      <rv-badge
        shape="icon"
        color="blue"
        icon="display"
        ariaLabel="Digital Signage"
        matTooltip="Digital Signage"
        matTooltipClass="rv-badge-tooltip"
      />
    }
    @if (hasInteractiveLicense(item)) {
      <rv-badge
        shape="icon"
        color="purple"
        icon="cursor-hand"
        ariaLabel="Interactive"
        matTooltip="Interactive"
        matTooltipClass="rv-badge-tooltip"
      />
    }
    @if (isFreeLicense(item)) {
      <span class="tw-text-green-700 tw-font-medium">Free</span>
    }
    @if (isScheduledUnlicensed(item)) {
      <rv-badge
        shape="icon"
        color="amber"
        icon="status-badge-scheduled-unlicensed"
        ariaLabel="Scheduled Unlicensed"
        matTooltip="Scheduled Unlicensed"
        matTooltipClass="rv-badge-tooltip"
      />
    } @else if (
      !hasDigitalSignageLicense(item) &&
      !hasInteractiveLicense(item) &&
      !isFreeLicense(item)
    ) {
      <span class="tw-text-gray-700">−</span>
    }
  </div>
</ng-template>

<ng-template
  #lastConnection
  let-lastActivityDate="lastActivityDate"
  let-onlineStatus="onlineStatus"
>
  @if (onlineStatus === 'online') {
    <span class="tw-text-gray-700">Connected</span>
  } @else if (!lastActivityDate) {
    <span class="tw-text-gray-700">−</span>
  } @else {
    <change-details [changeDate]="lastActivityDate"></change-details>
  }
</ng-template>
